<template>
  <!--
    A water tank, drawn in the valve's and the pump's idiom: one inline SVG on a
    48-grid in `currentColor`, the vessel's wall semi-transparent so the water
    inside it stays visible, and the water itself drawn to the level the device
    reports — never a fixed half.

    The drawing is built in three layers so the seven shapes share one language:
    the vessel and its outside fittings at the back, the water (one surface, or
    the plant's stepped chambers), then what sits IN the water — a float riding
    the surface, the buffer's baffles, the treatment plant's chamber walls and
    its rising air. An alarm tints the water red; the treatment plant's blower
    turns and its bubbles rise only while the blower runs.
  -->
  <svg
    class="tank-anim"
    :class="[`tank-anim--${kind}`, { 'tank-anim--alarm': alarm, 'tank-anim--active': active }]"
    viewBox="0 0 48 48"
    aria-hidden="true"
    focusable="false">
    <defs>
      <clipPath :id="clipId">
        <path :d="shape.clip" />
      </clipPath>
    </defs>

    <!-- ── The vessel and its outside fittings ─────────────────────────────── -->
    <g v-if="kind === 'ibc'">
      <rect class="tank-anim__pallet" x="7" y="41.4" width="34" height="4" rx="1.2" />
      <path class="tank-anim__slat" d="M11,41.6 V45.2 M17,41.6 V45.2 M23,41.6 V45.2 M29,41.6 V45.2 M35,41.6 V45.2" />
      <path class="tank-anim__steel" d="M9,5.6 V41.4 M39,5.6 V41.4 M9,5.6 H39 M9,24 H39" />
      <path class="tank-anim__steel" d="M15,6 V41 M21,6 V41 M27,6 V41 M33,6 V41" />
      <rect class="tank-anim__shell" x="11.6" y="7.8" width="24.8" height="32.2" rx="2.4" />
      <rect class="tank-anim__casing" x="20.6" y="2.8" width="6.8" height="2.6" rx="1" />
      <circle class="tank-anim__casing" cx="24" cy="2.6" r="1.6" />
      <rect class="tank-anim__accent" x="21.6" y="40" width="4.8" height="2.6" rx="1" />
      <path class="tank-anim__wall" d="M26.4,41.3 H32" />
      <path class="tank-anim__tick" d="M39.8,14 H41.6 M39.8,24 H41.6 M39.8,34 H41.6" />
    </g>

    <g v-else-if="kind === 'cistern'">
      <rect class="tank-anim__steel" x="13" y="43.4" width="22" height="2.6" rx="1" />
      <rect class="tank-anim__shell" x="13.8" y="10" width="20.4" height="33" rx="10.2" />
      <rect class="tank-anim__casing" x="11" y="7.2" width="26" height="3.2" rx="1.6" />
      <rect class="tank-anim__casing" x="21" y="3.6" width="6" height="3.8" rx="1" />
      <rect class="tank-anim__casing" x="19.4" y="1.4" width="9.2" height="2.4" rx="1.2" />
      <rect class="tank-anim__accent" x="30.6" y="40.6" width="3" height="3" rx="0.8" />
      <path class="tank-anim__wall" d="M33.6,42.1 H37.6" />
      <path class="tank-anim__tick" d="M10.8,18 H12.6 M10.8,26 H12.6 M10.8,34 H12.6" />
    </g>

    <g v-else-if="kind === 'horizontal'">
      <rect class="tank-anim__steel" x="10.4" y="34.6" width="5.4" height="8.8" rx="1" />
      <rect class="tank-anim__steel" x="32.2" y="34.6" width="5.4" height="8.8" rx="1" />
      <path class="tank-anim__wall" d="M7.4,43.4 H40.6" />
      <rect class="tank-anim__shell" x="6" y="15" width="36" height="22" rx="11" />
      <circle class="tank-anim__casing" cx="24" cy="15" r="3.6" />
      <circle class="tank-anim__seam" cx="24" cy="15" r="2.1" />
      <path class="tank-anim__pipe-thin" d="M12,15 V8.4" />
      <path class="tank-anim__wall" d="M10.4,15 V8.8 M13.6,15 V8.8" />
      <rect class="tank-anim__accent" x="42" y="23.4" width="3.2" height="5.2" rx="1" />
      <path class="tank-anim__wall" d="M45.2,24.6 V27.4" />
    </g>

    <g v-else-if="kind === 'buffer'">
      <rect class="tank-anim__casing" x="14" y="6.2" width="20" height="36.6" rx="4" />
      <rect class="tank-anim__shell" x="16.2" y="8.4" width="15.6" height="32" rx="3" />
      <path class="tank-anim__pipe-thin" d="M19.4,6.2 V1.6 M29,6.2 V1.6" />
      <path class="tank-anim__wall" d="M17.4,6.2 V2 M21.4,6.2 V2 M27,6.2 V2 M31,6.2 V2" />
      <path class="tank-anim__pipe-thin" d="M14,19 H8.6 M34,34 H39.4" />
      <path class="tank-anim__wall" d="M14,16.6 H9 M14,21.4 H9 M34,31.6 H39 M34,36.4 H39" />
      <rect class="tank-anim__casing" x="35.2" y="12" width="3" height="14" rx="1.5" />
      <circle class="tank-anim__accent" cx="36.7" cy="27.4" r="2" />
      <rect class="tank-anim__accent" x="36.05" y="18" width="1.3" height="8" rx="0.65" />
    </g>

    <g v-else-if="kind === 'treatment'">
      <rect class="tank-anim__shell" x="5" y="17.6" width="38" height="16.8" rx="8.4" />
      <rect class="tank-anim__casing" x="8" y="13.6" width="8" height="4" rx="1.4" />
      <rect class="tank-anim__casing" x="20" y="13.6" width="8" height="4" rx="1.4" />
      <rect class="tank-anim__casing" x="32" y="13.6" width="8" height="4" rx="1.4" />
      <path class="tank-anim__pipe-thin" d="M12,13.6 V7.6" />
      <path class="tank-anim__wall" d="M10.6,13.6 V8 M13.4,13.6 V8" />
      <rect class="tank-anim__motor" x="32.8" y="4.2" width="10.8" height="6.6" rx="2" />
      <path class="tank-anim__seam" d="M36.6,10.8 V13.6" />
      <path class="tank-anim__wall" d="M4.6,26 H1.6 M43.4,26 H46.4" />
    </g>

    <g v-else-if="kind === 'septic'">
      <path class="tank-anim__wall" d="M2.4,17.4 H45.6" />
      <path class="tank-anim__shell" d="M9,34.4 V23.6 A15,7.6 0 0 1 39,23.6 V34.4 Z" />
      <circle class="tank-anim__casing" cx="24" cy="17.4" r="3.2" />
      <path class="tank-anim__seam" d="M30,18.6 V9.6" />
      <path class="tank-anim__casing" d="M28.6,9.6 H31.4" />
      <path class="tank-anim__wall" d="M9,24.4 H3.4 M39,26.6 H45" />
    </g>

    <g v-else>
      <rect class="tank-anim__shell" x="15.4" y="8.4" width="17.2" height="31.4" rx="8.6" />
      <path class="tank-anim__pipe-thin" d="M24,8.4 V2.4 M24,39.8 V45.6" />
      <path class="tank-anim__wall" d="M22.6,8.4 V2.8 M25.4,8.4 V2.8 M22.6,39.8 V45.2 M25.4,39.8 V45.2" />
      <rect class="tank-anim__steel" x="20" y="39.8" width="8" height="2.4" rx="1" />
      <circle class="tank-anim__gauge" cx="29.2" cy="5" r="2.8" />
      <path class="tank-anim__needle" d="M29.2,5 L30.8,3.4" />
      <path class="tank-anim__wall" d="M26.4,5.2 H25.4" />
    </g>

    <!-- ── The water, drawn to the level the device reports ────────────────── -->
    <template v-if="kind === 'treatment'">
      <g :clip-path="`url(#${clipId})`">
        <rect
          v-for="(surface, index) in treatmentSurfaces"
          :key="`fill-${index}`"
          class="tank-anim__body-fill"
          :x="TREATMENT_CHAMBERS[index].x"
          :y="surface"
          :width="TREATMENT_CHAMBERS[index].w"
          height="48" />
      </g>
      <line
        v-for="(surface, index) in treatmentSurfaces"
        :key="`surface-${index}`"
        class="tank-anim__surface"
        :x1="TREATMENT_CHAMBERS[index].x"
        :x2="TREATMENT_CHAMBERS[index].x + TREATMENT_CHAMBERS[index].w"
        :y1="surface"
        :y2="surface"
        :clip-path="`url(#${clipId})`" />
    </template>

    <template v-else-if="kind === 'septic'">
      <g :clip-path="`url(#${clipId})`">
        <rect class="tank-anim__body-fill" x="2" :y="septicSurfaces[0]" width="22" height="48" />
        <rect class="tank-anim__body-fill" x="24" :y="septicSurfaces[1]" width="22" height="48" />
      </g>
      <line
        class="tank-anim__surface"
        x1="4"
        x2="24"
        :y1="septicSurfaces[0]"
        :y2="septicSurfaces[0]"
        :clip-path="`url(#${clipId})`" />
      <line
        class="tank-anim__surface"
        x1="24"
        x2="44"
        :y1="septicSurfaces[1]"
        :y2="septicSurfaces[1]"
        :clip-path="`url(#${clipId})`" />
    </template>

    <template v-else>
      <g :clip-path="`url(#${clipId})`">
        <rect class="tank-anim__body-fill" x="2" :y="surfaceY" width="44" :height="48 - surfaceY" />
        <g class="tank-anim__wave-group" :style="{ transform: `translateY(${surfaceY}px)` }">
          <path class="tank-anim__wave" d="M2,0 q4,-1.5 8,0 t8,0 t8,0 t8,0 t8,0" />
        </g>
        <path class="tank-anim__current" d="M16,0 V48" />
        <path class="tank-anim__current tank-anim__current--b" d="M32,0 V48" />
      </g>
      <line class="tank-anim__surface" x1="2" x2="46" :y1="surfaceY" :y2="surfaceY" :clip-path="`url(#${clipId})`" />
    </template>

    <!-- ── What sits in the water ─────────────────────────────────────────── -->
    <g v-if="kind === 'buffer'">
      <path class="tank-anim__seam" d="M16.6,14 H31.4 M16.6,20 H31.4" />
    </g>

    <g v-else-if="kind === 'treatment'">
      <path class="tank-anim__steel" d="M19,17.6 V34.4 M31,17.6 V34.4" />
      <g class="tank-anim__blower" :style="{ transformOrigin: '38.2px 7.5px' }">
        <path class="tank-anim__impeller" d="M38.2,5.2 V9.8 M35.9,7.5 H40.5" />
      </g>
      <circle class="tank-anim__bubble" cx="23" cy="31.4" r="1" />
      <circle class="tank-anim__bubble" cx="27" cy="29.6" r="1.2" />
      <circle class="tank-anim__bubble" cx="25" cy="32.6" r="0.8" />
    </g>

    <g v-else-if="kind === 'septic'">
      <path class="tank-anim__steel" d="M24,24.6 V34.4" />
    </g>

    <path
      v-else-if="kind === 'expansion'"
      class="tank-anim__membrane"
      :d="`M15.4,${surfaceY} Q24,${surfaceY + 2.4} 32.6,${surfaceY}`" />

    <!-- The float riding the surface — the tank's own "this is live" gesture -->
    <circle
      v-if="floatX !== null && fill > 0.03"
      class="tank-anim__float"
      :cx="floatX"
      :cy="surfaceY - 1.5"
      r="1.8" />

    <!-- An alarm is the one thing a tank shouts about: level or fault -->
    <circle v-if="alarm" class="tank-anim__alarm" cx="6.4" cy="5.6" r="2.2" />
  </svg>
</template>

<script lang="ts">
/** Module scope: the water clip id must be unique per instance. */
let instanceSeq = 0;
</script>

<script setup lang="ts">
import { computed } from 'vue';
import type { TankKind } from '../deviceWidgetTypes';

const props = withDefaults(
  defineProps<{
    kind?: TankKind;
    /** The level the tank reports, 0–100; `null` draws it empty. */
    level?: number | null;
    /** Low level / fault — the water and the alarm dot turn red. */
    alarm?: boolean;
    /** The plant's blower is running — its vanes turn and the air rises. */
    active?: boolean;
  }>(),
  { kind: 'cistern', level: null, alarm: false, active: false },
);

const uid = `tank-anim-${++instanceSeq}`;
const clipId = `${uid}-water`;

function roundedRect(x: number, y: number, w: number, h: number, r: number): string {
  const rad = Math.min(r, w / 2, h / 2);
  return [
    `M${x + rad},${y}`,
    `H${x + w - rad}`,
    `A${rad},${rad} 0 0 1 ${x + w},${y + rad}`,
    `V${y + h - rad}`,
    `A${rad},${rad} 0 0 1 ${x + w - rad},${y + h}`,
    `H${x + rad}`,
    `A${rad},${rad} 0 0 1 ${x},${y + h - rad}`,
    `V${y + rad}`,
    `A${rad},${rad} 0 0 1 ${x + rad},${y}`,
    'Z',
  ].join(' ');
}

/** Inset by a millimetre, so the water never paints over its own wall. */
function inner(x: number, y: number, w: number, h: number, r: number): string {
  return roundedRect(x + 0.9, y + 0.9, w - 1.8, h - 1.8, Math.max(0.6, r - 0.9));
}

/**
 * Each shape's water volume and the span its level runs over. The clip is the
 * inner face of the vessel; `top`/`bottom` are where 100 % and 0 % sit.
 */
const SHAPES: Record<TankKind, { top: number; bottom: number; clip: string }> = {
  ibc: { top: 8.6, bottom: 39.4, clip: inner(11.6, 7.8, 24.8, 32.2, 2.4) },
  cistern: { top: 11, bottom: 42.4, clip: inner(13.8, 10, 20.4, 33, 10.2) },
  horizontal: { top: 15.6, bottom: 36.4, clip: inner(6, 15, 36, 22, 11) },
  buffer: { top: 9, bottom: 39.8, clip: inner(16.2, 8.4, 15.6, 32, 3) },
  treatment: { top: 18.2, bottom: 33.8, clip: inner(5, 17.6, 38, 16.8, 8.4) },
  septic: { top: 24.4, bottom: 34, clip: 'M9.9,33.7 V24 A14.1,6.9 0 0 1 38.1,24 V33.7 Z' },
  expansion: { top: 9, bottom: 39.2, clip: inner(15.4, 8.4, 17.2, 31.4, 8.6) },
};

/** The three chambers of the treatment plant, left to right. */
const TREATMENT_CHAMBERS = [
  { x: 2, w: 17 },
  { x: 19, w: 12 },
  { x: 31, w: 15 },
];

const shape = computed(() => SHAPES[props.kind]);

const fill = computed(() => {
  if (props.level === null || props.level === undefined) return 0;
  const value = Number(props.level);
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value / 100));
});

/** Where the surface sits — the level's own y on the drawing. */
const surfaceY = computed(() => {
  const { top, bottom } = shape.value;
  return bottom - (bottom - top) * fill.value;
});

/**
 * The plant's chambers are drawn stepped off the one reading (the module's own
 * `CHAMBER_RISE`): aeration a little higher, the clarifier a little lower, so
 * three chambers read as a plant processing water rather than as a bottle.
 */
const treatmentSurfaces = computed(() => {
  const { top, bottom } = shape.value;
  const span = bottom - top;
  const level = fill.value;
  return [
    bottom - span * Math.min(1, level + 0.06),
    bottom - span * level,
    bottom - span * Math.max(0, level - 0.04),
  ];
});

/** The septic vault's two chambers, the second settling a little lower. */
const septicSurfaces = computed(() => {
  const { top, bottom } = shape.value;
  const span = bottom - top;
  return [bottom - span * fill.value, bottom - span * Math.max(0, fill.value - 0.05)];
});

/** Where the float rides — the shapes whose surface is one open sheet. */
const FLOAT_X: Partial<Record<TankKind, number>> = {
  ibc: 19.6,
  cistern: 24,
  horizontal: 17.4,
  buffer: 24,
};

const floatX = computed(() => FLOAT_X[props.kind] ?? null);
</script>

<style scoped>
.tank-anim {
  display: block;
  width: 100%;
  height: 100%;
}

/* ── The vessel: metal and semi-transparent, so the water shows through it ── */
.tank-anim__shell {
  fill: color-mix(in srgb, currentColor 16%, transparent);
  stroke: color-mix(in srgb, currentColor 70%, transparent);
  stroke-width: 1.2;
}

.tank-anim__casing {
  fill: color-mix(in srgb, currentColor 44%, transparent);
  stroke: color-mix(in srgb, currentColor 78%, rgb(0 0 0));
  stroke-width: 1.1;
}

.tank-anim__motor {
  fill: color-mix(in srgb, currentColor 52%, transparent);
  stroke: color-mix(in srgb, currentColor 80%, rgb(0 0 0));
  stroke-width: 1.1;
}

.tank-anim__steel {
  fill: none;
  stroke: color-mix(in srgb, currentColor 58%, transparent);
  stroke-width: 1.1;
  stroke-linecap: round;
}

.tank-anim__wall {
  fill: none;
  stroke: color-mix(in srgb, currentColor 74%, transparent);
  stroke-width: 1.2;
  stroke-linecap: round;
}

.tank-anim__seam {
  fill: none;
  stroke: color-mix(in srgb, currentColor 62%, transparent);
  stroke-width: 0.9;
  stroke-linecap: round;
}

.tank-anim__tick {
  fill: none;
  stroke: color-mix(in srgb, currentColor 52%, transparent);
  stroke-width: 1;
  stroke-linecap: round;
}

.tank-anim__pipe-thin {
  fill: none;
  stroke: color-mix(in srgb, currentColor 40%, transparent);
  stroke-width: 3.6;
}

.tank-anim__pallet {
  fill: color-mix(in srgb, currentColor 55%, rgb(0 0 0));
}

.tank-anim__slat {
  fill: none;
  stroke: color-mix(in srgb, currentColor 34%, transparent);
  stroke-width: 1;
}

/* ── Water: a blue body drawn to its level, a surface, and a slow drift ───── */
.tank-anim__body-fill {
  fill: #38bdf8;
  fill-opacity: 0.5;
  transition:
    y 0.55s ease,
    height 0.55s ease;
}

.tank-anim__surface {
  stroke: #7dd3fc;
  stroke-width: 1.4;
  stroke-linecap: round;
  transition:
    y1 0.55s ease,
    y2 0.55s ease;
}

.tank-anim__wave-group {
  transition: transform 0.55s ease;
}

.tank-anim__wave {
  fill: none;
  stroke: #e0f2fe;
  stroke-width: 1.2;
  opacity: 0.85;
  animation: tank-anim-bob 2.8s ease-in-out infinite;
}

.tank-anim__current {
  fill: none;
  stroke: #e0f2fe;
  stroke-width: 1.2;
  stroke-linecap: round;
  stroke-dasharray: 2 7;
  opacity: 0.55;
  animation: tank-anim-drift 9s linear infinite;
}

.tank-anim__current--b {
  animation-delay: -4.5s;
}

.tank-anim__float {
  fill: #f59e0b;
  stroke: #b45309;
  stroke-width: 0.7;
  transition: cy 0.55s ease;
}

.tank-anim__accent {
  fill: #f59e0b;
}

.tank-anim__membrane {
  fill: none;
  stroke: color-mix(in srgb, currentColor 74%, rgb(0 0 0));
  stroke-width: 1.3;
  stroke-linecap: round;
}

.tank-anim__gauge {
  fill: #f8fafc;
  stroke: color-mix(in srgb, currentColor 70%, rgb(0 0 0));
  stroke-width: 0.9;
}

.tank-anim__needle {
  fill: none;
  stroke: #dc2626;
  stroke-width: 1.1;
  stroke-linecap: round;
}

/* ── Alarm: the water itself says it, and the dot pulses ──────────────────── */
.tank-anim__alarm {
  fill: #ef4444;
  animation: tank-anim-alarm 1.5s ease-in-out infinite;
}

.tank-anim--alarm .tank-anim__body-fill {
  fill: #f87171;
  fill-opacity: 0.45;
}

.tank-anim--alarm .tank-anim__surface,
.tank-anim--alarm .tank-anim__wave {
  stroke: #ef4444;
}

/* ── The treatment plant's blower: air only while it runs ────────────────── */
.tank-anim__impeller {
  fill: none;
  stroke: #f59e0b;
  stroke-width: 1.3;
  stroke-linecap: round;
}

.tank-anim__bubble {
  fill: #7dd3fc;
  opacity: 0;
}

.tank-anim--active .tank-anim__blower {
  animation: tank-anim-spin 1.4s linear infinite;
}

.tank-anim--active .tank-anim__bubble {
  opacity: 0.9;
  animation: tank-anim-bubble 2.2s ease-in infinite;
}

.tank-anim--active .tank-anim__bubble:nth-child(3) {
  animation-delay: 0.7s;
}

.tank-anim--active .tank-anim__bubble:nth-child(4) {
  animation-delay: 1.3s;
}

@keyframes tank-anim-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes tank-anim-bob {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(1.1px);
  }
}

@keyframes tank-anim-drift {
  to {
    stroke-dashoffset: -9;
  }
}

@keyframes tank-anim-bubble {
  0% {
    opacity: 0;
    transform: translateY(0);
  }
  30% {
    opacity: 0.9;
  }
  100% {
    opacity: 0;
    transform: translateY(-7px);
  }
}

@keyframes tank-anim-alarm {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}

/* Reduced motion: the drawing stays, the movement stops. */
@media (prefers-reduced-motion: reduce) {
  .tank-anim__wave,
  .tank-anim__current,
  .tank-anim__alarm,
  .tank-anim--active .tank-anim__blower,
  .tank-anim--active .tank-anim__bubble {
    animation: none !important;
  }
}
</style>
